import { LABELS } from './flatfileLabels'
import getMaxCharValidator from '../utils/getMaxCharValidator'
import emptyValueValidator from '../utils/emptyValueValidator'
import getColumnWidth from '../utils/getFlatfileColumnWidth'
import getTPAConstraint from '../utils/getTPAConstraint'
import validateEINRegex from '../utils/validateEIN'
import validatePlanNumber from '../utils/getPlanNumberValidator'
import createNamePrefixValidator from '../utils/getNamePrefixValidator'
import createNotApplicableValidator from '../utils/getNotApplicableValidator'
import { notificationServicesFields } from './notificationsFields'

export const getNotificationsParticipantFields = (userAudience) => [
  {
    key: 'recordkeeper',
    label: LABELS.RECORDKEEPER,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'recordKeeper',
        config: {
          noSlashRegex: {
            regex: '\\/|\\\\',
            error: 'Please enter a valid Recordkeeper. No slashes allowed.',
          },
          createMaxCharValidator: getMaxCharValidator(60),
          createEmptyValueValidator: emptyValueValidator('Recordkeeper'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.RECORDKEEPER),
    },
  },
  {
    key: 'tpa',
    label: LABELS.THIRD_PARTY_ADMINISTRATOR,
    type: 'string',
    constraints: getTPAConstraint(userAudience),
    appearance: {
      size: getColumnWidth(LABELS.THIRD_PARTY_ADMINISTRATOR),
    },
  },
  {
    key: 'planSponsor',
    label: LABELS.PLAN_SPONSOR,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'planSponsor',
        config: {
          noSlashRegex: {
            regex: '\\/|\\\\',
            error: 'Please enter a valid Plan Sponsor. No slashes allowed.',
          },
          invalidString: {
            regex:
              '^\\s*(Plan Sponsor|Plan Sponsor Name|Company|Company Name)\\s*$',
            error: 'Please type a valid plan sponsor name',
          },
          createMaxCharValidator: getMaxCharValidator(60),
          createEmptyValueValidator: emptyValueValidator('Plan sponsor'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.PLAN_SPONSOR),
    },
  },
  {
    key: 'planName',
    label: LABELS.COMPLETE_LEGAL_PLAN_NAME,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'planName',
        config: {
          noSlashRegex: {
            regex: '\\/|\\\\',
            error: 'Please enter a valid Plan Name. No slashes allowed.',
          },
          createMaxCharValidator: getMaxCharValidator(80),
          createEmptyValueValidator: emptyValueValidator(
            'Complete legal plan name'
          ),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.COMPLETE_LEGAL_PLAN_NAME),
    },
  },
  {
    key: 'planEin',
    label: LABELS.EMPLOYER_IDENTIFICATION_NUMBER,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'planEin',
        config: validateEINRegex(),
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.EMPLOYER_IDENTIFICATION_NUMBER),
    },
  },
  {
    key: 'planNumber',
    label: LABELS.THREE_DIGIT_PLAN_NUMBER,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'planNumber',
        config: validatePlanNumber(),
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.THREE_DIGIT_PLAN_NUMBER),
    },
  },
  {
    key: 'planIdNumber',
    label: LABELS.PROVIDER_PLAN_ID_NUMBER,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'planIdNumber',
        config: {
          createMaxCharValidator: getMaxCharValidator(30),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.PROVIDER_PLAN_ID_NUMBER),
    },
  },
  {
    key: 'firstName',
    label: LABELS.FIRST_NAME,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'firstName',
        config: {
          createMaxCharValidator: getMaxCharValidator(40),
          checkNamePrefix: createNamePrefixValidator(),
          checkNotApplicable: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator('First name'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.FIRST_NAME),
    },
  },
  {
    key: 'middleName',
    label: LABELS.MIDDLE_NAME,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'middleName',
        config: {
          createMaxCharValidator: getMaxCharValidator(20),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.MIDDLE_NAME),
    },
  },
  {
    key: 'lastName',
    label: LABELS.LAST_NAME,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'lastName',
        config: {
          createMaxCharValidator: getMaxCharValidator(60),
          checkNotApplicable: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator('Last name'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.LAST_NAME),
    },
  },
  {
    key: 'addressLine1',
    label: LABELS.ADDRESS_LINE_1,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'addressLine1',
        config: {
          createMaxCharValidator: getMaxCharValidator(36),
          checkNotApplicable: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator('Address line 1'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.ADDRESS_LINE_1),
    },
  },
  {
    key: 'addressLine2',
    label: LABELS.ADDRESS_LINE_2,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'addressLine2',
        config: {
          createMaxCharValidator: getMaxCharValidator(36),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.ADDRESS_LINE_2),
    },
  },
  {
    key: 'city',
    label: LABELS.CITY,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'city',
        config: {
          createMaxCharValidator: getMaxCharValidator(22),
          createEmptyValueValidator: emptyValueValidator('City'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.CITY),
    },
  },
  {
    key: 'state',
    label: LABELS.STATE,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'state',
        config: {
          createMaxCharValidator: getMaxCharValidator(22),
          createEmptyValueValidator: emptyValueValidator('State'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.STATE),
    },
  },
  {
    key: 'zipCode',
    label: LABELS.ZIP_CODE,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'zipCode',
        config: {
          createMaxCharValidator: getMaxCharValidator(10),
          createEmptyValueValidator: emptyValueValidator('Zip code'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.ZIP_CODE),
    },
  },
  {
    key: 'countryCode',
    label: LABELS.COUNTRY_CODE,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'countryCode',
        config: {
          createEmptyValueValidator: emptyValueValidator('Country code'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.COUNTRY_CODE),
    },
  },
  {
    key: 'emailAddress',
    label: LABELS.EMAIL_ADDRESS,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'emailAddress',
        config: {
          createMaxCharValidator: getMaxCharValidator(50),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.EMAIL_ADDRESS),
    },
  },
  {
    key: notificationServicesFields.BAL_VESTED.key,
    label: notificationServicesFields.BAL_VESTED.label,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: notificationServicesFields.BAL_VESTED.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(14),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.BAL_VESTED.label),
    },
  },
  {
    key: notificationServicesFields.BAL_TOTAL.key,
    label: notificationServicesFields.BAL_TOTAL.label,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: notificationServicesFields.BAL_TOTAL.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(14),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.BAL_TOTAL.label),
    },
  },
  {
    key: notificationServicesFields.DATE_OF_BALANCE.key,
    label: notificationServicesFields.DATE_OF_BALANCE.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.DATE_OF_BALANCE.label),
    },
  },
  {
    key: notificationServicesFields.REASON_ROLLOUT_REQ.key,
    label: notificationServicesFields.REASON_ROLLOUT_REQ.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: notificationServicesFields.REASON_ROLLOUT_REQ.key,
        config: {
          reasonRolloutReq: {
            regex: '^(?:SBR|TERM)$',
            error:
              'Please specify if this is for a terminating plan (TERM) or small balance rollout (SBR)',
          },
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.REASON_ROLLOUT_REQ.label),
    },
  },
  {
    key: notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key,
    label: notificationServicesFields.GRACE_PERIOD_TO_RESPOND.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
        config: {
          message: 'Value must be 30, 35, 40, 45, 60, or 90',
          level: 'error',
        },
      },
      {
        type: 'external',
        validator: notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key,
        config: {
          gracePeriodToRespond: {
            regex: '^(?:30|35|40|45|60|90)$',
            error:
              '30, 35, 40, 45, 60, or 90 days the participant has to respond to the rollout notice',
          },
        },
      },
    ],
    appearance: {
      size: getColumnWidth(
        notificationServicesFields.GRACE_PERIOD_TO_RESPOND.label
      ),
    },
  },
  {
    key: notificationServicesFields.COMPANY_NAME.key,
    label: notificationServicesFields.COMPANY_NAME.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_NAME.key,
        config: {
          noSlashRegex: {
            regex: '\\/|\\\\',
            error: 'Please enter a valid Company name. No slashes allowed.',
          },
          createMaxCharValidator: getMaxCharValidator(40),
          createEmptyValueValidator: emptyValueValidator(
            notificationServicesFields.COMPANY_NAME.label
          ),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.COMPANY_NAME.label),
    },
  },
  {
    key: notificationServicesFields.COMPANY_ADDRESS_1.key,
    label: notificationServicesFields.COMPANY_ADDRESS_1.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_ADDRESS_1.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(36),
          createNotApplicableValidator: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator(
            notificationServicesFields.COMPANY_ADDRESS_1.label
          ),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.COMPANY_ADDRESS_1.label),
    },
  },
  {
    key: notificationServicesFields.COMPANY_ADDRESS_2.key,
    label: notificationServicesFields.COMPANY_ADDRESS_2.label,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_ADDRESS_2.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(36),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.COMPANY_ADDRESS_2.label),
    },
  },
  {
    key: notificationServicesFields.COMPANY_CITY.key,
    label: notificationServicesFields.COMPANY_CITY.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_CITY.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(22),
          createEmptyValueValidator: emptyValueValidator(
            notificationServicesFields.COMPANY_CITY.label
          ),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.COMPANY_CITY.label),
    },
  },
  {
    key: notificationServicesFields.COMPANY_STATE.key,
    label: notificationServicesFields.COMPANY_STATE.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_STATE.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(22),
          createEmptyValueValidator: emptyValueValidator(
            notificationServicesFields.COMPANY_STATE.label
          ),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.COMPANY_STATE.label),
    },
  },
  {
    key: notificationServicesFields.COMPANY_ZIP.key,
    label: notificationServicesFields.COMPANY_ZIP.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_ZIP.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(10),
          createEmptyValueValidator: emptyValueValidator(
            notificationServicesFields.COMPANY_ZIP.label
          ),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.COMPANY_ZIP.label),
    },
  },
  {
    key: notificationServicesFields.COMPANY_CONTACT_PHONE.key,
    label: notificationServicesFields.COMPANY_CONTACT_PHONE.label,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_CONTACT_PHONE.key,
        config: {
          createEmptyValueValidator: emptyValueValidator(
            notificationServicesFields.COMPANY_CONTACT_PHONE.label
          ),
          companyContactPhone: {
            regex: '^(\\+|\\(|[0-9]){1}[0-9a-zA-Z ,\\.\\(\\)-]{2,35}$',
            error:
              'Must start with +, (, or a number and must follow a standard formatting convention. E.g., +01 (555) 243-0321 ext. 44',
          },
        },
      },
    ],
    appearance: {
      size: getColumnWidth(
        notificationServicesFields.COMPANY_CONTACT_PHONE.label
      ),
    },
  },
  {
    key: notificationServicesFields.COMPANY_WEBSITE.key,
    label: notificationServicesFields.COMPANY_WEBSITE.label,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_WEBSITE.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(36),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(notificationServicesFields.COMPANY_WEBSITE.label),
    },
  },
  {
    key: notificationServicesFields.COMPANY_CONTACT_EMAIL.key,
    label: notificationServicesFields.COMPANY_CONTACT_EMAIL.label,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: notificationServicesFields.COMPANY_CONTACT_EMAIL.key,
        config: {
          createMaxCharValidator: getMaxCharValidator(50),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(
        notificationServicesFields.COMPANY_CONTACT_EMAIL.label
      ),
    },
  },
]
